import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Form, Row, Col, Spinner, Table } from "react-bootstrap";
import axios from "axios";
import { Link } from "react-router-dom";
import { FiEdit2, FiEye, FiSearch, FiX } from "react-icons/fi";
import PageLayout from "../../layouts/PageLayout";
import {
    formatOrderMoney,
    getSellerOrderHeaders,
    sellerOrderApiUrl,
    unwrapSellerOrders,
} from "./sellerOrderUtils";

const statusVariant = (status) => ({
    Pending: "warning",
    Shipped: "primary",
    Delivered: "success",
    Returned: "danger",
}[status] || "secondary");
const deliveryStatuses = ["Pending", "Shipped", "Delivered", "Returned"];

export default function StoreSellerOrdersPage() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [deliveryFilter, setDeliveryFilter] = useState("");

    const loadOrders = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get(`${sellerOrderApiUrl}/store-seller/my-orders`, {
                headers: getSellerOrderHeaders(),
            });
            setOrders(unwrapSellerOrders(response));
        } catch (requestError) {
            console.error("Could not load seller orders:", requestError);
            setError(requestError.response?.data?.message || "Could not load your orders.");
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    const filteredOrders = useMemo(() => {
        const query = search.trim().toLowerCase();
        return orders.filter((order) => {
            if (deliveryFilter && (order.delivery_status || "Pending") !== deliveryFilter) return false;
            if (!query) return true;

            const products = Array.isArray(order.products) ? order.products : [];
            return [
                order.order_number,
                order.buyer?.name,
                order.buyer?.email,
                order.buyer?.phone,
                order.tracking_number,
                ...products.flatMap((line) => [line.product?.title, line.product?.product_code]),
            ].some((value) => String(value || "").toLowerCase().includes(query));
        });
    }, [orders, search, deliveryFilter]);

    return (
        <PageLayout>
            <div className="mc-card mb-4">
                <div className="mc-breadcrumb d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                        <h3 className="mc-breadcrumb-title">My Store Orders</h3>
                        <p className="text-muted mb-0">Review buyer details, fulfillment status, and order totals.</p>
                    </div>
                    {!loading && <Badge bg="primary" className="fs-6">{filteredOrders.length} of {orders.length} orders</Badge>}
                </div>
            </div>
            {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" /></div>
                    ) : orders.length === 0 ? (
                        <div className="text-center py-5">
                            <span className="material-icons text-muted mb-2" style={{ fontSize: 42 }}>receipt_long</span>
                            <p className="text-muted mb-0">No orders include your store products.</p>
                        </div>
                    ) : (
                        <>
                        <Row className="g-3 p-4 pb-3 align-items-end">
                            <Col md={8} lg={9}>
                                <Form.Label htmlFor="seller-order-search">Filter orders</Form.Label>
                                <div className="position-relative">
                                    <FiSearch className="position-absolute text-muted" style={{ left: 12, top: 12 }} aria-hidden="true" />
                                    <Form.Control
                                        id="seller-order-search"
                                        type="search"
                                        value={search}
                                        onChange={(event) => setSearch(event.target.value)}
                                        placeholder="Search order number, buyer, product code, or tracking number"
                                        className="ps-5 pe-5"
                                    />
                                    {search && (
                                        <button
                                            type="button"
                                            className="btn btn-link text-muted position-absolute p-0"
                                            style={{ right: 12, top: 9 }}
                                            onClick={() => setSearch("")}
                                            aria-label="Clear order search"
                                        >
                                            <FiX />
                                        </button>
                                    )}
                                </div>
                            </Col>
                            <Col md={4} lg={3}>
                                <Form.Label htmlFor="seller-order-delivery-filter">Delivery status</Form.Label>
                                <Form.Select
                                    id="seller-order-delivery-filter"
                                    value={deliveryFilter}
                                    onChange={(event) => setDeliveryFilter(event.target.value)}
                                >
                                    <option value="">All statuses</option>
                                    {deliveryStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
                                </Form.Select>
                            </Col>
                        </Row>
                        <div className="table-responsive">
                            <Table hover responsive className="align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Order</th>
                                        <th>Buyer</th>
                                        <th>Items</th>
                                        <th>Payment</th>
                                        <th>Your items ($)</th>
                                        <th>Your payout ($)</th>
                                        <th>Delivery</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredOrders.map((order) => {
                                        const productLines = Array.isArray(order.products) ? order.products : [];
                                        const itemCount = productLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
                                        const productNames = productLines.slice(0, 2).map((line) => (
                                            `${line.product?.title || "Product"}${line.product?.product_code ? ` (${line.product.product_code})` : ""}`
                                        ));
                                        const extraCount = Math.max(productLines.length - productNames.length, 0);

                                        return (
                                            <tr key={order.id}>
                                                <td>
                                                    <strong>{order.order_number || `#${order.id}`}</strong>
                                                    <small className="d-block text-muted">ID: {order.id}</small>
                                                </td>
                                                <td>
                                                    <strong>{order.buyer?.name || "Buyer"}</strong>
                                                    <small className="d-block text-muted">{order.buyer?.email || order.buyer?.phone || "Contact unavailable"}</small>
                                                </td>
                                                <td>
                                                    <span>{itemCount} item{itemCount === 1 ? "" : "s"}</span>
                                                    <small className="d-block text-muted">
                                                        {productNames.join(", ")}{extraCount ? ` +${extraCount} more` : ""}
                                                    </small>
                                                </td>
                                                <td>
                                                    <span>{order.payment_status || "—"}</span>
                                                </td>
                                                <td className="fw-semibold">{formatOrderMoney(order.items_total_usd)}</td>
                                                <td>
                                                    {(() => {
                                                        const payouts = productLines.map((line) => line.payout).filter(Boolean);
                                                        if (!payouts.length) return <small className="text-muted">-</small>;
                                                        const first = payouts[0];
                                                        return (
                                                            <>
                                                                <span className="fw-semibold">{formatOrderMoney(order.payout_total_usd)}</span>
                                                                <small className="d-block text-muted">Fee {Number(first.fee_percent)}%: {formatOrderMoney(order.fee_total_usd)}</small>
                                                                <small className="d-block" title={first.hold_reason || ""}>{first.status_label}{first.hold_until && ["on_hold"].includes(first.status) ? ` until ${first.hold_until}` : ""}</small>
                                                            </>
                                                        );
                                                    })()}
                                                </td>
                                                <td>
                                                    <Badge bg={statusVariant(order.delivery_status)}>{order.delivery_status || "Pending"}</Badge>
                                                    {order.tracking_number && <small className="d-block text-muted mt-1">{order.tracking_number}</small>}
                                                </td>
                                                <td className="text-end text-nowrap">
                                                    <Button
                                                        as={Link}
                                                        to={`/seller-store-orders/${order.id}`}
                                                        state={{ order }}
                                                        size="sm"
                                                        variant="primary"
                                                        className="me-2 d-inline-flex align-items-center justify-content-center"
                                                        style={{ width: 38, height: 38 }}
                                                        title="View order"
                                                        aria-label={`View order ${order.order_number || order.id}`}
                                                    >
                                                        <FiEye size={18} aria-hidden="true" />
                                                    </Button>
                                                    <Button
                                                        as={Link}
                                                        to={`/seller-store-orders/${order.id}/edit`}
                                                        state={{ order }}
                                                        size="sm"
                                                        variant="secondary"
                                                        className="d-inline-flex align-items-center justify-content-center"
                                                        style={{ width: 38, height: 38 }}
                                                        title="Edit order"
                                                        aria-label={`Edit order ${order.order_number || order.id}`}
                                                    >
                                                        <FiEdit2 size={18} aria-hidden="true" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {!filteredOrders.length && (
                                        <tr><td colSpan={7} className="text-center py-4 text-muted">No orders match these filters.</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        </>
                    )}
                </Card.Body>
            </Card>
        </PageLayout>
    );
}
